import React, { useEffect, useState } from 'react';
import './App.css';

function PrivacyPolicy({ onBack }) {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="App">
      <div className="container legal-page-container">
        {/* Header */}
        <div className="legal-header">
          <div className="legal-header-top">
            <div className="logo-wrapper">
              <div className="logo-icon">
                <svg width="120" height="60" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Algodentra logo">
                  <g>
                    <path d="M10 30 L20 10 L30 30 Z" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="38" y1="20" x2="38" y2="20" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="43" y1="15" x2="43" y2="25" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="48" y1="10" x2="48" y2="30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="53" y1="12" x2="53" y2="28" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="58" y1="18" x2="58" y2="22" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="63" y1="14" x2="63" y2="26" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="68" y1="16" x2="68" y2="24" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="73" y1="20" x2="73" y2="20" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  </g>
                </svg>
              </div>
              <h1 className="title">Algodentra</h1>
            </div>
            <button className="auth-back-button" onClick={onBack}>
              <span className="auth-back-icon">←</span>
              Back to login
            </button>
          </div>
          <div className="divider"></div>
        </div>

        {/* Content */}
        <div className="legal-content">
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-date"><strong>Last Updated:</strong> November 9, 2025</p>

          <div className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Algodentra ("we," "our," or "us"). We are committed to protecting your privacy and the confidentiality of your personal health information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered SOAP note generation platform.
            </p>
            <p>
              <strong>Important:</strong> We are <strong>not yet operating on a HIPAA-BAA certified infrastructure</strong>. The product is built for privacy-by-design (local processing + PII redaction) but full HIPAA/PIPEDA/PHIPA compliance will only be claimed after we deploy to a BAA-backed environment post-funding. Until then, do not treat the platform as a HIPAA-covered service and avoid storing PHI that requires a BAA.
            </p>
          </div>

          <div className="legal-section">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Health Information</h3>
            <ul>
              <li>Audio recordings of patient consultations</li>
              <li>Transcribed patient-provider conversations</li>
              <li>SOAP notes (Subjective, Objective, Assessment, Plan)</li>
              <li>Patient names, dates of birth, and medical record numbers (when provided)</li>
              <li>Clinical observations, diagnoses, and treatment plans</li>
            </ul>

            <h3>2.2 Technical Information</h3>
            <ul>
              <li>Access codes and authentication credentials</li>
              <li>IP addresses and device information</li>
              <li>Usage data and system logs</li>
              <li>Browser type and operating system</li>
            </ul>

            <h3>2.3 Professional Information</h3>
            <ul>
              <li>Healthcare provider credentials</li>
              <li>Practice or facility information</li>
              <li>Professional contact details</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>3. How We Use Your Information</h2>

            <h3>3.1 Primary Purposes</h3>
            <ul>
              <li><strong>Clinical Documentation:</strong> Generate accurate SOAP notes from audio recordings</li>
              <li><strong>AI Processing:</strong> Transcribe audio and generate clinical recommendations</li>
              <li><strong>PII Redaction:</strong> Automatically identify and redact personally identifiable information</li>
              <li><strong>Quality Assurance:</strong> Improve AI accuracy and system performance</li>
            </ul>

            <h3>3.2 Security and Compliance</h3>
            <ul>
              <li>Authenticate and authorize users</li>
              <li>Detect and prevent unauthorized access</li>
              <li>Maintain audit logs for compliance</li>
              <li>Respond to security incidents</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>4. Data Processing and Third-Party Services</h2>

            <h3>4.1 AI Service Providers</h3>
            <p>We use the following third-party AI services to process your data:</p>
            <ul>
              <li><strong>Claude (Anthropic):</strong> SOAP note generation and clinical support (no BAA in place; do not send PHI to this cloud service until we complete a BAA-backed deployment)</li>
              <li><strong>Cohere API:</strong> PII redaction and data anonymization (no BAA in place; intended for de-identified content only)</li>
              <li><strong>Faster-Whisper (Local):</strong> Audio transcription
                <ul>
                  <li>Runs entirely on local servers</li>
                  <li>No data sent to external services</li>
                </ul>
              </li>
            </ul>

            <h3>4.2 Data Minimization</h3>
            <p>We follow data minimization principles:</p>
            <ul>
              <li>Only necessary PHI is processed through AI services</li>
              <li>PII is automatically redacted before SOAP generation</li>
              <li>Audio files are processed locally when possible</li>
              <li>Temporary data is deleted immediately after processing</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>5. Data Security</h2>

            <h3>5.1 Technical Safeguards</h3>
            <ul>
              <li><strong>Encryption:</strong> All data encrypted using AES-256 at rest and TLS 1.3 in transit</li>
              <li><strong>Access Controls:</strong> Role-based access with multi-factor authentication</li>
              <li><strong>Audit Logging:</strong> Comprehensive logging of all data access and modifications</li>
              <li><strong>Secure Storage:</strong> We are not yet hosted on BAA-certified infrastructure; current environment is pre-production. PHI that requires a BAA should not be stored until we complete migration to a compliant stack.</li>
            </ul>

            <h3>5.2 Administrative Safeguards</h3>
            <ul>
              <li>Regular security assessments and penetration testing</li>
              <li>Employee training on privacy and security best practices</li>
              <li>Incident response and breach notification procedures</li>
              <li>BAA reviews are pending; vendors are treated as non-BAA until finalized</li>
            </ul>

            <h3>5.3 Physical Safeguards</h3>
            <ul>
              <li>Data centers with 24/7 security monitoring</li>
              <li>Redundant backup systems</li>
              <li>Disaster recovery and business continuity plans</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>6. Data Retention and Deletion</h2>

            <h3>6.1 Retention Periods</h3>
            <ul>
              <li><strong>SOAP Notes:</strong> Stored in our application database under your account until you delete them or request removal</li>
              <li><strong>Audio Recordings:</strong> Deleted from the server after processing (target 24 hours maximum)</li>
              <li><strong>Temporary Data:</strong> Deleted immediately after SOAP generation completes</li>
              <li><strong>Audit Logs:</strong> Retention policies are under review for future HIPAA alignment; current pre-production logs are limited to operational needs.</li>
            </ul>

            <h3>6.2 User Control</h3>
            <ul>
              <li>Users can delete SOAP notes at any time from the History section</li>
              <li>Request complete data deletion by contacting support</li>
              <li>Export your data before deletion (available on request)</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>7. Your Rights</h2>

            <h3>7.1 HIPAA Rights</h3>
            <ul>
              <li><strong>Right to Access:</strong> Request copies of your PHI</li>
              <li><strong>Right to Amend:</strong> Request corrections to your PHI</li>
              <li><strong>Right to Accounting:</strong> Receive an accounting of PHI disclosures</li>
              <li><strong>Right to Restrict:</strong> Request restrictions on PHI use</li>
            </ul>

            <h3>7.2 PIPEDA/PHIPA Rights (Canadian Users)</h3>
            <ul>
              <li>Right to know what personal information we hold</li>
              <li>Right to access your personal information</li>
              <li>Right to correct inaccuracies</li>
              <li>Right to withdraw consent (where applicable)</li>
              <li>Right to file a complaint with privacy commissioners</li>
            </ul>

            <h3>7.3 How to Exercise Your Rights</h3>
            <p>To exercise any of these rights, contact us at:</p>
            <ul>
              <li><strong>Email:</strong> privacy@pearlaicare.com</li>
              <li><strong>Response Time:</strong> Within 30 days of request</li>
              <li><strong>Verification:</strong> Identity verification required for all requests</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>8. Cookies and Tracking</h2>
            <p>
              <strong>We do NOT use cookies or tracking technologies.</strong> The only data stored locally is:
            </p>
            <ul>
              <li>Authentication token (JWT) in localStorage for session continuity</li>
              <li>No analytics, advertising, or third-party tracking</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>9. Children's Privacy</h2>
            <p>
              Algodentra is designed for use by healthcare professionals only. We do not knowingly collect information from children under 13 (or 16 in certain jurisdictions). If you believe a child has provided us with personal information, please contact us immediately.
            </p>
          </div>

          <div className="legal-section">
            <h2>10. International Data Transfers</h2>
            <p>
              Your data may be processed in servers located in the United States and Canada. We ensure all transfers comply with:
            </p>
            <ul>
              <li>Standard Contractual Clauses (SCCs) for international transfers</li>
              <li>GDPR adequacy decisions (where applicable)</li>
              <li>PIPEDA cross-border data transfer requirements</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>11. Breach Notification</h2>
            <p>
              In the event of a data breach affecting PHI, we will:
            </p>
            <ul>
              <li>Notify affected individuals within 60 days</li>
              <li>Report to relevant authorities as required by law</li>
              <li>Provide details of the breach, affected data, and mitigation steps</li>
              <li>Offer credit monitoring if applicable</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically. We will:
            </p>
            <ul>
              <li>Notify you of material changes via email</li>
              <li>Update the "Last Updated" date at the top</li>
              <li>Provide 30 days notice before changes take effect</li>
              <li>Obtain new consent if required by law</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>13. Contact Information</h2>

            <h3>Privacy Officer</h3>
            <p>
              <strong>Email:</strong> privacy@pearlaicare.com<br />
              <strong>Response Time:</strong> Within 5 business days
            </p>

            <h3>Data Protection Officer (DPO)</h3>
            <p>
              <strong>Email:</strong> dpo@pearlaicare.com
            </p>

            <h3>Regulatory Complaints</h3>
            <ul>
              <li><strong>US (HIPAA):</strong> Office for Civil Rights - <a href="https://www.hhs.gov/ocr/complaints" target="_blank" rel="noopener noreferrer">www.hhs.gov/ocr/complaints</a></li>
              <li><strong>Canada (PIPEDA):</strong> Office of the Privacy Commissioner - <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer">www.priv.gc.ca</a></li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>14. Consent</h2>
            <p>
              By using Algodentra, you consent to this Privacy Policy. If you do not agree, please do not use our services.
            </p>
            <p>
              <strong>For healthcare providers:</strong> You are responsible for obtaining patient consent for recording and processing their health information through this platform.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="legal-footer">
          <p>&copy; 2025 Algodentra. All rights reserved.</p>
          <p>HIPAA Compliant | PIPEDA Compliant | PHIPA Compliant</p>
        </div>
      </div>

      {showScrollTop && (
        <button
          className="scroll-to-top-btn"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
}

export default PrivacyPolicy;
